import React, { useRef, useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';
import { Manager, Reference, Popper } from 'react-popper';
import { Button } from '../Button';
import { CloseButton } from '../CloseButton';
import { Heading } from '../Heading';
import { TAB_KEYCODE } from '../Utils/constant';
import styles from '@dtsl/css-popover/dist/index.module.css';
const TriggerType = {
  hover: 'hover',
  focus: 'focus',
  click: 'click'
};

// eslint-disable-next-line react/display-name
const Popover = /*#__PURE__*/forwardRef((_ref, ref) => {
  let {
    title,
    visible: show = false,
    placement,
    children,
    onMouseEnter,
    onMouseLeave,
    onFocus,
    onBlur,
    trigger,
    offset,
    className,
    secondaryAction,
    primaryAction,
    content,
    timeout,
    onClick,
    handleClose,
    popoverHeaderClass,
    popoverFooterClass
  } = _ref;
  const isHoverOnly = trigger.length === 1 && trigger.includes(TriggerType.hover);
  const [visible, setVisible] = useState(show);
  const popoverBodyRef = useRef(null);
  useEffect(() => {
    if (visible !== show) {
      setVisible(show);
    }
  }, [show]);
  useEffect(() => {
    if (visible && popoverBodyRef.current) {
      var _popoverBodyRef$curre, _focusableItems$;
      const focusableItems = (_popoverBodyRef$curre = popoverBodyRef.current.parentNode) === null || _popoverBodyRef$curre === void 0 ? void 0 : _popoverBodyRef$curre.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
      (_focusableItems$ = focusableItems[0]) === null || _focusableItems$ === void 0 ? void 0 : _focusableItems$.focus({
        preventScroll: true
      });
    }
  }, [visible]);

  //Document outside click
  useEffect(() => {
    document.addEventListener('click', handleDocumentClick);
    document.addEventListener('keydown', handleTabKey);
    return () => {
      document.removeEventListener('click', handleDocumentClick);
      document.removeEventListener('keydown', handleTabKey);
    };
  }, [visible, isHoverOnly, timeout]);
  useImperativeHandle(ref, () => ({
    showPopover: () => {
      showPopover();
    },
    hidePopover: () => {
      hidePopover();
    }
  }));
  const showPopover = () => {
    if (timeout) {
      setTimeout(() => {
        setVisible && setVisible(true);
      }, timeout);
    } else {
      setVisible(true);
    }
  };
  const hidePopover = () => {
    if (timeout) {
      setTimeout(() => {
        setVisible && setVisible(false);
      }, timeout);
    } else {
      setVisible(false);
    }
  };
  const togglePopover = () => {
    if (!visible) {
      if (timeout) {
        setTimeout(() => {
          setVisible && setVisible(true);
        }, timeout);
      } else {
        setVisible(true);
      }
    } else {
      if (timeout) {
        setTimeout(() => {
          setVisible && setVisible(false);
        }, timeout);
      } else {
        setVisible(false);
      }
    }
  };
  const handleMouseEnter = function (e) {
    let onTriggerMouseEnter = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : () => {};
    e.preventDefault();
    trigger.includes(TriggerType.hover) && showPopover();
    onTriggerMouseEnter(e);
    onMouseEnter(e);
  };
  const handleMouseLeave = function (e) {
    let onTriggerMouseLeave = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : () => {};
    e.preventDefault();
    isHoverOnly && hidePopover();
    onTriggerMouseLeave(e);
    onMouseLeave(e);
  };
  const handleFocus = function (e) {
    let onTriggerFocus = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : () => {};
    e.preventDefault();
    trigger.includes(TriggerType.focus) && showPopover();
    onTriggerFocus(e);
    onFocus(e);
  };
  const handleClick = function (e) {
    let onTriggerClick = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : () => {};
    e.preventDefault();
    trigger.includes(TriggerType.click) && togglePopover();
    onTriggerClick(e);
    onClick(e);
  };
  const handleCloseButton = event => {
    event.stopPropagation();
    event.preventDefault();
    event.nativeEvent.stopImmediatePropagation();
    hidePopover();
    handleClose();
  };
  const handleDocumentClick = event => {
    var _children$props, _event$target$closest;
    const triggerId = children === null || children === void 0 ? void 0 : (_children$props = children.props) === null || _children$props === void 0 ? void 0 : _children$props.id;
    const targetId = (_event$target$closest = event.target.closest('.sib-popover-trigger')) === null || _event$target$closest === void 0 ? void 0 : _event$target$closest.id;
    if (!isHoverOnly) {
      if (visible && popoverBodyRef.current && !popoverBodyRef.current.parentElement.contains(event.target) && triggerId != targetId) {
        hidePopover();
      }
    }
  };

  //Focus trap on popover
  const handleTabKey = event => {
    let isTabPressed = event.keyCode === TAB_KEYCODE || event.key === 'Tab';
    if (popoverBodyRef && popoverBodyRef.current) {
      const focusableItems = popoverBodyRef.current.parentNode.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
      if (focusableItems.length > 0) {
        const first = focusableItems[0]; // get first element to be focused inside modal
        const last = focusableItems[focusableItems.length - 1]; // get last element to be focused inside modal
        if (!isTabPressed) {
          return;
        }
        if (event.shiftKey) {
          // if shift key pressed for shift + tab combination
          if (document.activeElement === first) {
            last.focus(); // add focus for the last focusable element
            event.preventDefault();
          }
        } else {
          // if tab key is pressed
          if (document.activeElement === last) {
            // if focused has reached to last focusable element then focus first focusable element after pressing tab
            first.focus(); // add focus for the first focusable element
            event.preventDefault();
          }
        }
      }
    }
  };
  const headerClass = classNames({
    [styles['sib-popover-header']]: true,
    [popoverHeaderClass]: !!popoverHeaderClass
  });
  const footerClass = classNames({
    [styles['sib-popover-footer']]: true,
    [popoverFooterClass]: !!popoverFooterClass
  });
  const popoverWrapperClass = classNames({
    [styles['sib-popover']]: true,
    [styles['sib-popover-empty-header']]: isHoverOnly && !title,
    [className]: !!className
  });
  const popoverContent = typeof content === 'function' ? content() : content;
  return /*#__PURE__*/React.createElement(Manager, null, /*#__PURE__*/React.createElement(Reference, null, _ref2 => {
    var _children$props2;
    let {
      ref
    } = _ref2;
    return /*#__PURE__*/React.cloneElement(children, {
      ref,
      className: classNames({
        [children.props.className]: !!(children !== null && children !== void 0 && (_children$props2 = children.props) !== null && _children$props2 !== void 0 && _children$props2.className),
        'sib-popover-trigger': true
      }),
      onClick: e => {
        var _children$props3;
        return handleClick(e, children === null || children === void 0 ? void 0 : (_children$props3 = children.props) === null || _children$props3 === void 0 ? void 0 : _children$props3.onClick);
      },
      onMouseEnter: e => {
        var _children$props4;
        return handleMouseEnter(e, children === null || children === void 0 ? void 0 : (_children$props4 = children.props) === null || _children$props4 === void 0 ? void 0 : _children$props4.onMouseEnter);
      },
      onMouseLeave: e => {
        var _children$props5;
        return handleMouseLeave(e, children === null || children === void 0 ? void 0 : (_children$props5 = children.props) === null || _children$props5 === void 0 ? void 0 : _children$props5.onMouseLeave);
      },
      onFocus: e => {
        var _children$props6;
        return handleFocus(e, children === null || children === void 0 ? void 0 : (_children$props6 = children.props) === null || _children$props6 === void 0 ? void 0 : _children$props6.onFocus);
      },
      onBlur,
      'data-is-panel-open': visible ? 'true' : 'false'
    });
  }), visible ? /*#__PURE__*/React.createElement(Popper, {
    strategy: "absolute",
    placement: placement,
    eventsEnabled: false,
    modifiers: [{
      name: 'offset',
      options: {
        offset
      }
    }]
  }, _ref3 => {
    let {
      ref,
      style,
      placement
    } = _ref3;
    return /*#__PURE__*/ReactDOM.createPortal( /*#__PURE__*/React.createElement("div", {
      ref: ref,
      id: "popover",
      role: "dialog",
      "aria-labelledby": "popover-heading",
      "aria-describedby": "popover-body",
      tabIndex: "-1",
      style: style,
      className: popoverWrapperClass,
      "data-placement": placement
    }, /*#__PURE__*/React.createElement("div", {
      id: "popover-heading",
      className: headerClass
    }, title && /*#__PURE__*/React.createElement(Heading, {
      className: styles['sib-popover-header-title'],
      size: "sm"
    }, title), !isHoverOnly && /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement(CloseButton, {
      onClick: handleCloseButton,
      className: styles['sib-popover-header-close-btn']
    }))), /*#__PURE__*/React.createElement("div", {
      id: "popover-body",
      ref: popoverBodyRef,
      className: styles['sib-popover-content']
    }, popoverContent), /*#__PURE__*/React.createElement("footer", {
      className: footerClass
    }, secondaryAction && !isHoverOnly ? /*#__PURE__*/React.createElement(Button, secondaryAction) : null, primaryAction && !isHoverOnly ? /*#__PURE__*/React.createElement(Button, primaryAction) : null)), document.body);
  }) : null);
});
Popover.defaultProps = {
  title: '',
  className: '',
  placement: 'bottom',
  visible: false,
  offset: [0, 8],
  trigger: [TriggerType.hover, TriggerType.focus],
  onMouseEnter: () => {},
  onMouseLeave: () => {},
  onFocus: () => {},
  onBlur: () => {},
  timeout: null,
  onClick: () => {},
  primaryAction: null,
  secondaryAction: null,
  popoverHeaderClass: '',
  popoverFooterClass: '',
  content: null,
  handleClose: () => {}
};
Popover.propTypes = {
  title: PropTypes.string,
  visible: PropTypes.bool,
  className: PropTypes.string,
  children: PropTypes.node.isRequired,
  placement: PropTypes.oneOf(['top', 'right', 'bottom', 'left', 'top-end', 'top-start', 'bottom-end', 'bottom-start', 'left-end', 'left-start', 'right-end', 'right-start']),
  onMouseEnter: PropTypes.func,
  onMouseLeave: PropTypes.func,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  offset: PropTypes.array,
  trigger: PropTypes.array,
  timeout: PropTypes.number,
  onClick: PropTypes.func,
  popoverHeaderClass: PropTypes.string,
  popoverFooterClass: PropTypes.string,
  primaryAction: PropTypes.shape({
    variant: PropTypes.oneOf(['primary', 'secondary', 'tertiary', 'destructive']),
    iconAlone: PropTypes.bool,
    label: PropTypes.string,
    icon: PropTypes.node,
    ellipsis: PropTypes.bool,
    disabled: PropTypes.bool,
    tooltip: PropTypes.bool,
    tooltipValue: PropTypes.object,
    dropdownOpen: PropTypes.bool,
    showArrowIcon: PropTypes.bool,
    className: PropTypes.string
  }),
  secondaryAction: PropTypes.shape({
    variant: PropTypes.oneOf(['primary', 'secondary', 'tertiary', 'destructive']),
    label: PropTypes.string,
    iconAlone: PropTypes.bool,
    icon: PropTypes.node,
    ellipsis: PropTypes.bool,
    disabled: PropTypes.bool,
    tooltip: PropTypes.bool,
    tooltipValue: PropTypes.object,
    dropdownOpen: PropTypes.bool,
    showArrowIcon: PropTypes.bool,
    className: PropTypes.string
  }),
  content: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  handleClose: PropTypes.func
};
export default Popover;